import { ContentType } from './ContentType';

export enum PermissionEnum {
  COVENANTS = 'ui:publisher:covenants',
  COVENANTS_DOC_SCORES_CUSIP_NA = 'ui:covenants:doc_scores:cusip:na',
  COVENANTS_DOC_SCORES_LOANX_EU = 'ui:covenants:doc_scores:loanx:eu',
  COVENANTS_DOC_SCORES_LOANX_NA = 'ui:covenants:doc_scores:loanx:na',
  NEWS = 'ui:publisher:news',
  RESEARCH = 'ui:publisher:research',
  STAFF = 'ui:admin:edit_articles',
  REDISTRIBUTION_ALLOWED = 'ui:redistribution_allowed',
  PRINT = 'ui:print',
  DOWNLOAD_CONTENT = 'ui:download_content',
  SHARE_CONTENT = 'ui:share_content',
  ASK_AN_ANALYST = 'ui:ask_an_analyst',
  COPY_PASTE = 'ui:copy_paste',
  RISK_PRODUCTS_EXTERNAL_LINK = 'ui:risk_products:external_link',
  RISK_PRODUCTS_ENTITLEMENT_LEVEL_2 = 'risk_products:level_2',
  RISK_PRODUCTS_ENTITLEMENT_LEVEL_3 = 'risk_products:level_3',
  FINANCIALS = 'ui:financials',
  FINANCIALS_DOWNLOAD_EXCEL = 'ui:financials:download:excel',
  UI_ADMIN_USER_ADMIN = 'ui:admin:user_admin',
  // temp feature permissions
  RISK_PRODUCTS = 'ui:risk_products',
  RISK_PRODUCTS_API = 'api:risk_products:all',
  DOCKETS = 'ui:dockets',
  EXPORT = 'ui:export',
  ACCESS_USER_ADMIN = 'ui:access_user_admin',
  TRANSLATION = 'ui:translation',
  ACCESS_NOT_PROD_READY = 'ui:access_not_prod_ready',
}

export const CONTENTTYPE_PERMISSIONS_MAP = {
  [ContentType.Research]: PermissionEnum.RESEARCH,
  [ContentType.Covenants]: PermissionEnum.COVENANTS,
  [ContentType.News]: PermissionEnum.NEWS,
};

export enum UserSubscription {
  CS_CR_LFI = 'CS_CR_LFI',
  CS_CR = 'CS_CR',
  CS_LFI = 'CS_LFI',
  CR_LFI = 'CR_LFI',
  CS = 'CS',
  CR = 'CR',
  LFI = 'LFI',

  // TODO: FCI-1860
  // Having all possible product combinations does not scale as number of combinations grows exponentially (2^n - 1).
  // Initial implementation supported 3 products resulting in 7 combinations, the next iteration supports 4 products,
  // that should have resulted in 15 combinations, but it was simplified to 8.
  //
  //
  // In practice, adding additional subscription (RP) to original (CS, LFI, CS) subscriptions (for example CS vs CS + RP, CS + LFI vs CS + LFI + RP, ect..)
  // does not affect the logic where UserSubscription is used, for example on feeds page user with CS + LFI subscription has have widgets as CS + LFI + RP
  //
  // Some parts of application (mainly creditsights-web) have a dedicated UI configuration (e.g. Widgets on feeds page, navigation items) for each subscription combination.
  //
  // That's why additional combinations were not added for new products (RP) because it would require adding additional 7 enum values and redundant UI configurations in multiple places for each of the new combinations.
  //
  // This NEEDS to be refactored and all usages of UserSubscription needs to be removed. Unfortunately UserSubscription is widely used so it to stay as is for a time being.
  //
  // Please see implementation of `getPermissions` in entitlement.service in this repo to see the logic that resolves "user subscription" based on user permissions.
  // Please see FEED_ORDER_BY_ENTITLEMENTS_CONFIG, DEFAULT_COMPANY_WIDGETS_ORDER_CONFIG in creditsights-web repo to see how dedicated UI configuration are defined for each subscription combination.
  RP = 'RP',
}

export interface PermissionDto {
  id: string;
  name: string;
  code: string;
}

export class UserPermissions {
  constructor(public permissions: string[], /** @deprecated */ public userSubscription: UserSubscription) {}

  hasAccessToDataTools(): boolean {
    return this.includes(PermissionEnum.COVENANTS);
  }

  hasAccessToMarkets(): boolean {
    return this.includes(PermissionEnum.RESEARCH);
  }

  hasAccessToMarketStats(): boolean {
    return this.includes(PermissionEnum.NEWS);
  }

  hasAccessToSpecialSituations(): boolean {
    return this.includesSome(PermissionEnum.RESEARCH, PermissionEnum.NEWS);
  }

  hasAccessToDealsPipeline(): boolean {
    return this.includesSome(PermissionEnum.RESEARCH, PermissionEnum.NEWS, PermissionEnum.COVENANTS);
  }

  hasAccessToRP(): boolean {
    return this.includes(PermissionEnum.RISK_PRODUCTS) && this.includes(PermissionEnum.RISK_PRODUCTS_API);
  }

  hasAccessToSectors(): boolean {
    return this.includesSome(PermissionEnum.RESEARCH, PermissionEnum.NEWS, PermissionEnum.COVENANTS);
  }

  hasAccessToNotProdReady(): boolean {
    return this.includes(PermissionEnum.ACCESS_NOT_PROD_READY);
  }

  hasAccessToMyContentArticles(): boolean {
    return this.includesSome(PermissionEnum.RESEARCH, PermissionEnum.NEWS, PermissionEnum.COVENANTS);
  }

  hasAccessToStandardAlerts(): boolean {
    return this.includesSome(PermissionEnum.RESEARCH, PermissionEnum.NEWS, PermissionEnum.COVENANTS);
  }

  /**
   * Checks if user effective permissions includes given permission
   * @param permission permission to check
   * @returns
   */
  includes(permission: PermissionEnum): boolean {
    return this.permissions?.includes(permission);
  }

  /**
   * Checks if user effective permissions includes at least one of given permissions
   * @param permission permissions to check
   * @returns
   */
  includesSome(...permissions: PermissionEnum[]): boolean {
    return permissions.some((p) => this.includes(p));
  }
}
